import { useRef, useState, useMemo } from 'react';
import { IoCloudUploadOutline } from 'react-icons/io5';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Form, Row, Col, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sk } from 'date-fns/locale';
import { addPackagingUnit, fetchPackagingUnits } from '../../../store/actions/packagingUnit';
import { Loader } from '../../../utils/tools';
import { PACKAGING_TYPE_OPTIONS } from '../../../utils/constants/packagingTypes';
import { PACKAGING_CHARACTER_OPTIONS } from '../../../utils/constants/packagingCharacters';
import { EU_COUNTRIES } from '../../../utils/constants/euCountries';
import { getRoleBadges } from '../../../utils/constants/packagingRoles';
import { BASE_LANGUAGE, getMarketLanguages } from '../../../utils/constants/marketLanguages';
import LanguageSwitch from './languageSwitch';
import Select from 'react-select';
import { formValues, validation } from './validationSchema';

const ACCEPTED_PHOTO_TYPES = '.jpg,.jpeg,.png,.webp';

const AddPackagingUnit = ({ onClose, onBack, baseRole, producerRole, defaultValues, cloneOf }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [photo, setPhoto] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [textLang, setTextLang] = useState(BASE_LANGUAGE);

    const handleFile = (file) => {
        if (file && file.type.startsWith('image/')) setPhoto(file);
    };

    const formik = useFormik({
        initialValues: {
            ...formValues,
            baseRole: baseRole ?? '',
            producerRole: producerRole ?? 'NOT_APPLICABLE',
            ...(defaultValues ?? {}),
        },
        validationSchema: validation,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            dispatch(addPackagingUnit({ values, photo }))
                .unwrap()
                .then(() => {
                    dispatch(fetchPackagingUnits());
                    resetForm();
                    setPhoto(null);
                    onClose();
                })
                .catch(() => {})
                .finally(() => setSubmitting(false));
        },
    });

    const marketLanguages = useMemo(() => getMarketLanguages(formik.values.marketCountries), [formik.values.marketCountries]);
    const activeLang = marketLanguages.includes(textLang) || textLang === BASE_LANGUAGE ? textLang : BASE_LANGUAGE;

    const getTranslatedValue = (field) => (
        activeLang === BASE_LANGUAGE ? formik.values[field] : (formik.values.translations?.[activeLang]?.[field] ?? '')
    );
    const setTranslatedValue = (field, value) => {
        if (activeLang === BASE_LANGUAGE) {
            formik.setFieldValue(field, value);
        } else {
            formik.setFieldValue('translations', {
                ...formik.values.translations,
                [activeLang]: { ...formik.values.translations?.[activeLang], [field]: value },
            });
        }
    };

    return (
        <Form onSubmit={formik.handleSubmit}>
            <h2>{cloneOf ? `Kopírovať: ${cloneOf}` : 'Pridať obalovú jednotku'}</h2>
            <hr />

            {onBack && (
                <Button variant="link" className="text-muted ps-0 mb-2" onClick={onBack}>
                    ← Späť na diagnostiku
                </Button>
            )}

            {baseRole && (
                <div className="mb-4 d-flex align-items-center gap-2">
                    <span className="text-muted small">Postavenie:</span>
                    {getRoleBadges(baseRole, producerRole).map((badge, i) => (
                        <span key={i} className={`badge bg-${badge.variant} fs-6`}>{badge.label}</span>
                    ))}
                </div>
            )}

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formName">
                        <Form.Label>Názov *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Názov obalovej jednotky"
                            {...formik.getFieldProps('name')}
                            isInvalid={formik.touched.name && !!formik.errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12} sm={6}>
                    <Form.Group className="mb-3" controlId="formCode">
                        <Form.Label>EAN / Interný kód</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Napr. 8594123456789"
                            {...formik.getFieldProps('code')}
                            isInvalid={formik.touched.code && !!formik.errors.code}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.code}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12} sm={6}>
                    <Form.Group className="mb-3" controlId="formGtin">
                        <Form.Label>GTIN obalu</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Napr. 05012345678900"
                            {...formik.getFieldProps('gtin')}
                            isInvalid={formik.touched.gtin && !!formik.errors.gtin}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.gtin}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12} sm={6}>
                    <Form.Group className="mb-3" controlId="formPackagingType">
                        <Form.Label>Typ obalu *</Form.Label>
                        <Form.Select
                            {...formik.getFieldProps('packagingType')}
                            isInvalid={formik.touched.packagingType && !!formik.errors.packagingType}
                        >
                            <option value="">Vyberte typ obalu</option>
                            {PACKAGING_TYPE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.packagingType}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12} sm={6}>
                    <Form.Group className="mb-3" controlId="formCharacter">
                        <Form.Label>Charakter obalu *</Form.Label>
                        <Form.Select
                            {...formik.getFieldProps('character')}
                            isInvalid={formik.touched.character && !!formik.errors.character}
                        >
                            <option value="">Vyberte charakter obalu</option>
                            {PACKAGING_CHARACTER_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{formik.errors.character}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <div className="d-flex flex-wrap gap-4">
                            <Form.Check
                                type="checkbox"
                                id="formFoodContact"
                                label="Kontakt s potravinou"
                                checked={formik.values.foodContact}
                                onChange={e => formik.setFieldValue('foodContact', e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                id="formRecyclable"
                                label="Určený na recykláciu"
                                checked={formik.values.recyclable}
                                onChange={e => formik.setFieldValue('recyclable', e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                id="formDeposit"
                                label="Zálohovany obal"
                                checked={formik.values.deposit}
                                onChange={e => formik.setFieldValue('deposit', e.target.checked)}
                            />
                        </div>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formUsage">
                        <Form.Label>Použitie</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                placeholder="Napr. obal na potraviny, prepravný obal..."
                                value={getTranslatedValue('usage')}
                                onChange={e => setTranslatedValue('usage', e.target.value)}
                                onBlur={() => formik.setFieldTouched('usage', true)}
                                isInvalid={activeLang === BASE_LANGUAGE && formik.touched.usage && !!formik.errors.usage}
                            />
                            <LanguageSwitch languages={marketLanguages} active={activeLang} onChange={setTextLang} />
                        </div>
                        {activeLang === BASE_LANGUAGE && formik.touched.usage && formik.errors.usage && (
                            <div className="invalid-feedback d-block">{formik.errors.usage}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formDigitalPassportUrl">
                        <Form.Label>Externý digitálny pas (URL)</Form.Label>
                        <Form.Control
                            type="url"
                            placeholder="https://..."
                            {...formik.getFieldProps('digitalPassportUrl')}
                            isInvalid={formik.touched.digitalPassportUrl && !!formik.errors.digitalPassportUrl}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.digitalPassportUrl}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formDescription">
                        <Form.Label>Popis obalu</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                placeholder="Stručný popis obalu..."
                                value={getTranslatedValue('description')}
                                onChange={e => setTranslatedValue('description', e.target.value)}
                                onBlur={() => formik.setFieldTouched('description', true)}
                                isInvalid={activeLang === BASE_LANGUAGE && formik.touched.description && !!formik.errors.description}
                            />
                            <LanguageSwitch languages={marketLanguages} active={activeLang} onChange={setTextLang} />
                        </div>
                        {activeLang === BASE_LANGUAGE && formik.touched.description && formik.errors.description && (
                            <div className="invalid-feedback d-block">{formik.errors.description}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formOperationDescription">
                        <Form.Label>Opis a vysvetlenie fungovania obalu</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Napr. mechanizmus uzatvárania, spôsob otvárania, funkčné prvky..."
                                value={getTranslatedValue('operationDescription')}
                                onChange={e => setTranslatedValue('operationDescription', e.target.value)}
                                onBlur={() => formik.setFieldTouched('operationDescription', true)}
                                isInvalid={activeLang === BASE_LANGUAGE && formik.touched.operationDescription && !!formik.errors.operationDescription}
                            />
                            <LanguageSwitch languages={marketLanguages} active={activeLang} onChange={setTextLang} />
                        </div>
                        {activeLang === BASE_LANGUAGE && formik.touched.operationDescription && formik.errors.operationDescription && (
                            <div className="invalid-feedback d-block">{formik.errors.operationDescription}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formInternalNote">
                        <Form.Label>Interná poznámka</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Poznámka viditeľná len vo vašej firme..."
                            {...formik.getFieldProps('internalNote')}
                            isInvalid={formik.touched.internalNote && !!formik.errors.internalNote}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.internalNote}</Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Trh uvedenia *</Form.Label>
                        <Select
                            isMulti
                            options={EU_COUNTRIES.map(c => ({ value: c.code, label: c.label }))}
                            value={formik.values.marketCountries.map(code => {
                                const c = EU_COUNTRIES.find(x => x.code === code);
                                return c ? { value: c.code, label: c.label } : null;
                            }).filter(Boolean)}
                            onChange={(selected) => formik.setFieldValue('marketCountries', selected ? selected.map(s => s.value) : [])}
                            onBlur={() => formik.setFieldTouched('marketCountries', true)}
                            placeholder="Vyhľadajte alebo vyberte krajinu..."
                            noOptionsMessage={() => 'Žiadne výsledky'}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: formik.touched.marketCountries && formik.errors.marketCountries ? '#dc3545' : base.borderColor,
                                    '&:hover': {
                                        borderColor: formik.touched.marketCountries && formik.errors.marketCountries ? '#dc3545' : base['&:hover']?.borderColor,
                                    },
                                }),
                            }}
                        />
                        {formik.touched.marketCountries && formik.errors.marketCountries && (
                            <div className="invalid-feedback d-block">{formik.errors.marketCountries}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12} sm={6}>
                    <Form.Group className="mb-3" controlId="formMarketDate">
                        <Form.Label>Dátum uvedenia na trh</Form.Label>
                        <DatePicker
                            selected={formik.values.marketDate ? new Date(formik.values.marketDate) : null}
                            onChange={(date) => formik.setFieldValue('marketDate', date)}
                            dateFormat="dd.MM.yyyy"
                            locale={sk}
                            placeholderText="dd.mm.rrrr"
                            className={`form-control${formik.touched.marketDate && formik.errors.marketDate ? ' is-invalid' : ''}`}
                        />
                        {formik.touched.marketDate && formik.errors.marketDate && (
                            <div className="invalid-feedback d-block">{formik.errors.marketDate}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Fotka obalu</Form.Label>
                        {photo ? (
                            <div className="d-flex align-items-center gap-3">
                                <img
                                    src={URL.createObjectURL(photo)}
                                    alt={photo.name}
                                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                                />
                                <div>
                                    <div className="fw-semibold">{photo.name}</div>
                                    <Form.Text className="text-muted">{(photo.size / 1024).toFixed(1)} KB</Form.Text>
                                    <div>
                                        <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => setPhoto(null)}>
                                            Odstrániť
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                                onDragLeave={() => setIsDragOver(false)}
                                onDrop={e => { e.preventDefault(); setIsDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                                onClick={() => fileInputRef.current.click()}
                                style={{
                                    border: `2px dashed ${isDragOver ? '#86b7fe' : '#ced4da'}`,
                                    borderRadius: '8px',
                                    padding: '24px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isDragOver ? '#f0f6ff' : '#fafafa',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPTED_PHOTO_TYPES}
                                    style={{ display: 'none' }}
                                    onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }}
                                />
                                <IoCloudUploadOutline size={40} style={{ color: '#adb5bd', marginBottom: 4 }} />
                                <div className="fw-semibold">Pretiahnite fotku sem</div>
                                <Form.Text className="text-muted">alebo kliknite pre výber — jpg, jpeg, png, webp</Form.Text>
                            </div>
                        )}
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-auto">
                <hr />
                <Col className="d-flex justify-content-end">
                    <Button
                        className="button-custom-large"
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? <Loader /> : 'Pridať obalovú jednotku'}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default AddPackagingUnit;
